import defaultTemplates from "@hosttools/core/shared/messageRuleTemplates";
import {groupBy} from "lodash";
import type React from "react";
import {memo, useCallback, useEffect, useState} from "react";

import type {MessageRuleCustom} from "../../services/messageRule";
import SelectGeneric from "../SelectGeneric";

type MessageTemplateOption = {
    value: string;
    label: string;
    type: string;
};

const defaultOptionsConst: MessageTemplateOption[] = [
    {
        value: "booking",
        label: "Booking Confirmation Message Template",
        type: "Templates"
    },
    {
        value: "inquiry",
        label: "Booking Inquiry Message Template",
        type: "Templates"
    },
    {
        value: "timedout",
        label: "Pre-approval Expired Message Template",
        type: "Templates"
    },
    {
        value: "pending",
        label: "Booking Request Message Template",
        type: "Templates"
    },
    {
        value: "checkin",
        label: "Check-In Message Template",
        type: "Templates"
    },
    {
        value: "checkup",
        label: "Check-Up Message Template",
        type: "Templates"
    },
    {
        value: "checkout",
        label: "Check-Out Message Template",
        type: "Templates"
    },
    {
        value: "takeOutTheTrash",
        label: "Take Out The Trash",
        type: "Templates"
    },
    {
        value: "review",
        label: "Review Guest Template",
        type: "Templates"
    },
    {
        value: "reviewReminder",
        label: "Remind Guest to Review Template",
        type: "Templates"
    },
    {
        value: "email",
        label: "Email Cleaning Service About New Booking Template",
        type: "Templates"
    },
    {
        value: "cleaningChange",
        label: "Email Cleaning Service About Check-Out Change Template",
        type: "Templates"
    },
    {
        value: "cleaningCancellation",
        label: "Email Cleaning Service About Cancellation Template",
        type: "Templates"
    },
    {
        value: "cleaningReminder",
        label: "Email Cleaning Service Reminder Template",
        type: "Templates"
    },
    {
        value: "sms",
        label: "SMS Cleaning Service About New Booking Template",
        type: "Templates"
    }
];

type TemplateDefaultOptionValue =
    | "booking"
    | "inquiry"
    | "timedout"
    | "pending"
    | "checkin"
    | "checkup"
    | "checkout"
    | "takeOutTheTrash"
    | "review"
    | "reviewReminder"
    | "email"
    | "cleaningChange"
    | "cleaningCancellation"
    | "cleaningReminder"
    | "sms";

const optionValues = defaultOptionsConst.map(option => option.value);

export type GroupType = {
    label: string;
    options: MessageTemplateOption[];
};

export type MessageRuleTemplate = Partial<MessageRuleCustom> & {
    template?: string;
    lastMinuteMessageIsTheSame?: boolean;
};

export type Props = {
    isLoading?: boolean;
    selectedValue: string;
    isDisabled?: boolean;
    messageRules: MessageRuleCustom[];
    onSelectedTemplate: (template: MessageRuleTemplate | undefined) => void;
};

function isDefaultTemplate(templateName: string): templateName is TemplateDefaultOptionValue {
    return optionValues.includes(templateName);
}

const SelectMessageTemplates: React.FC<Props> = ({
    isLoading,
    messageRules,
    selectedValue,
    isDisabled,
    onSelectedTemplate
}) => {
    const [selectedOption, setSelectedOption] = useState<MessageTemplateOption["value"]>();
    const [options, setOptions] = useState<GroupType[]>([
        {
            label: "Templates",
            options: defaultOptionsConst
        }
    ]);

    const selectTemplate = useCallback(
        (templateName: string) => {
            let template: MessageRuleTemplate | undefined = messageRules.find(messageRule => {
                return messageRule._id === templateName;
            });
            if (template) {
                delete template._id;
            } else {
                const customTemplate: MessageRuleTemplate = {
                    template: "custom",
                    title: "",
                    minutes: 1020,
                    days: -2,
                    event: "checkout",
                    reservationLength: 1,
                    message: "",
                    lastMinuteMessageEnabled: false,
                    lastMinuteMessageIsTheSame: false,
                    lastMinuteMessage: "",
                    reviewEnabled: false,
                    reviewMessage: "",
                    sendMessageAfterLeavingReview: false,
                    disableMessageAfterReview: false,
                    emailEnabled: false,
                    email: "",
                    smsEnabled: false,
                    sms: "",
                    delay: 0,
                    preapprove: false,
                    languagesEnabled: false,
                    _id: undefined
                };

                if (isDefaultTemplate(templateName)) {
                    template = defaultTemplates[templateName] || customTemplate;
                }
            }
            return template;
        },
        [messageRules]
    );

    const handleChange = useCallback(
        (value: string) => {
            setSelectedOption(value);
            if (value) {
                const template = selectTemplate(value);
                onSelectedTemplate(template);
            }
        },
        [onSelectedTemplate, selectTemplate]
    );

    useEffect(() => {
        function groupOptions(messageRules: MessageRuleCustom[]) {
            const templates = messageRules.reduce<MessageTemplateOption[]>((result, rule) => {
                result.push({
                    label: rule.title,
                    value: rule._id,
                    type: rule.type
                });
                return result;
            }, []);
            const groupedTemplates = groupBy(templates, "type");
            const newOptions: GroupType[] = [
                {
                    label: "Templates",
                    options: defaultOptionsConst
                }
            ];

            const keys = Object.keys(groupedTemplates);
            keys.reverse();
            keys.forEach(key => {
                newOptions.push({
                    label: key,
                    options: groupedTemplates[key]
                });
            });
            setOptions(newOptions);
        }
        groupOptions(messageRules);
    }, [messageRules]);

    useEffect(() => {
        const selectedOption = options.find(group => {
            return group.options.find(option => {
                return option.value === selectedValue;
            });
        });
        if (!selectedOption) {
            setSelectedOption(undefined);
        }
    }, [onSelectedTemplate, options, selectedValue]);

    return (
        <SelectGeneric
            isDisabled={isDisabled}
            isLoading={isLoading}
            options={options}
            placeholder="Templates..."
            testID="select-message-template"
            value={selectedOption}
            onChange={handleChange}
        />
    );
};

export default memo(SelectMessageTemplates);
